import * as React from 'react'

import styled from '@emotion/styled'

import ProgressCircular from '../ProgressCircular/ProgressCircular'

type LoadingImageProps = {
  alt: string
  placeholderHeight: string
  placeholderWidth?: string
  fetchpriority?: 'high' | 'low' | 'auto'
} & React.ImgHTMLAttributes<HTMLImageElement>

const LoadingPlaceholder = styled.div<{
  imageLoaded: boolean
  placeholderHeight: LoadingImageProps['placeholderHeight']
  placeholderWidth?: LoadingImageProps['placeholderWidth']
}>`
  align-items: center;
  background: ${props => props.theme.arcadeColors.semantic.backgroundApp};
  border-radius: ${props => props.theme.border.radius.medium};
  display: ${props => (props.imageLoaded ? 'none' : 'flex')};
  height: ${props => props.placeholderHeight};
  width: ${props => props.placeholderWidth || '100%'};
  justify-content: center;
`

const Image = styled.img<{ imageLoaded: boolean }>`
  display: ${props => (props.imageLoaded ? 'block' : 'none')};
`

/**
 * This displays a skeleton loading placeholder while the image is loading.
 * Since we don't know the image's rendered height until it has loaded,
 * use the placeholderHeight to fill the image's space.
 *
 * Example of how to use:
 * <LoadingImage
 *  alt={t('Example alt', { context: 'alt text for image' })}
 *  placeholderHeight="12.5rem"
 *  src={imageSrc}
 * />
 */
const ArcadeLoadingImage = ({ alt, placeholderHeight, placeholderWidth, onLoad, ...props }: LoadingImageProps) => {
  const [imageLoaded, setImageLoaded] = React.useState(false)

  const handleLoad: React.ReactEventHandler<HTMLImageElement> = event => {
    setImageLoaded(true)
    onLoad?.(event)
  }

  return (
    <>
      <LoadingPlaceholder
        imageLoaded={imageLoaded}
        placeholderHeight={placeholderHeight}
        placeholderWidth={placeholderWidth}
        data-testid="loading-placeholder"
      >
        <ProgressCircular loadingAriaLabel="loading image" size="medium" />
      </LoadingPlaceholder>
      <Image {...props} alt={alt} imageLoaded={imageLoaded} onLoad={handleLoad} />
    </>
  )
}

export default ArcadeLoadingImage
